import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Tooltip,
  IconButton,
  Fade,
  Switch,
  Alert,
  Snackbar,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import RefreshIcon from '@mui/icons-material/Refresh';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PublicIcon from '@mui/icons-material/Public';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import AddToCollectionDialog from './AddToCollectionDialog';
import PublicAccessPermissionIcons from './Common/PublicAccessPermissionIcons';
import { fetchCollections, fetchProjectCollections } from '../Utils/collectionsApi';
import {
  fetchProject,
  fetchProjectPdfVisionPageCrops,
  updateProjectPdfVisionPageCrops
} from '../Utils/projectDataApi';

const normalizePublicAccessLevel = (value) => {
  const normalized = String(value || 'view').trim().toLowerCase();
  return ['view', 'upload', 'view_upload'].includes(normalized) ? normalized : 'view';
};
const publicAccessFlagsFromLevel = (value) => {
  const normalized = normalizePublicAccessLevel(value);
  return {
    canView: normalized === 'view' || normalized === 'view_upload',
    canUpload: normalized === 'upload' || normalized === 'view_upload'
  };
};
const publicAccessLevelFromFlags = ({ canView, canUpload }) => {
  if (canView && canUpload) return 'view_upload';
  if (canUpload) return 'upload';
  return 'view';
};
const normalizePdfAutoRotatePreset = (value, fallback = 'fast') => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['off', 'fast', 'balanced', 'quality'].includes(normalized) ? normalized : fallback;
};

const SettingOthers = ({ caseid }) => {
  const { t } = useTranslation();
  const { ws } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const [multiDocsSplitEnabled, setMultiDocsSplitEnabled] = useState(false);
  const [processOnFileChangeEnabled, setProcessOnFileChangeEnabled] = useState(false);
  const [processOnFileChangeLoading, setProcessOnFileChangeLoading] = useState(true);
  const [preferImageCreationTime, setPreferImageCreationTime] = useState(false);
  const [preferImageCreationTimeLoading, setPreferImageCreationTimeLoading] = useState(true);
  const [projectPdfAutoRotatePreset, setProjectPdfAutoRotatePreset] = useState('fast');
  const [projectPdfAutoRotatePresetLoading, setProjectPdfAutoRotatePresetLoading] = useState(true);
  const [projectPdfAutoRotateOcrAvailable, setProjectPdfAutoRotateOcrAvailable] = useState(null);
  const [showCalendarWidget, setShowCalendarWidget] = useState(true);
  const [showCalendarWidgetLoading, setShowCalendarWidgetLoading] = useState(true);
  const [showTrendWidget, setShowTrendWidget] = useState(true);
  const [showTrendWidgetLoading, setShowTrendWidgetLoading] = useState(true);
  const [projectPdfVisionPageCrops, setProjectPdfVisionPageCrops] = useState(true);
  const [projectPdfVisionPageCropsLoading, setProjectPdfVisionPageCropsLoading] = useState(true);
  const [fileFilterLoading, setFileFilterLoading] = useState(true);
  const [fileFilterSaving, setFileFilterSaving] = useState(false);
  const [fileFilters, setFileFilters] = useState([]);
  const [savedFileFilters, setSavedFileFilters] = useState([]);
  const fileFilterNextId = useRef(1);
  const [isPublic, setIsPublic] = useState(false);
  const [isPublicLoading, setIsPublicLoading] = useState(true);
  const [publicAccessLevel, setPublicAccessLevel] = useState('view');
  const [publicAccessFlags, setPublicAccessFlags] = useState(publicAccessFlagsFromLevel('view'));
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Collection management state
  const [projectCollections, setProjectCollections] = useState([]);
  const [allCollections, setAllCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [addToCollectionDialogOpen, setAddToCollectionDialogOpen] = useState(false);
  const [publicWarningDialogOpen, setPublicWarningDialogOpen] = useState(false);

  // Trigger animation when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loadOtherSettings = useCallback(() => {
    if (ws) {
      // request multiple 'other' settings for this case
      try {
        setFileFilterLoading(true);
        setShowCalendarWidgetLoading(true);
        setShowTrendWidgetLoading(true);
        setProjectPdfVisionPageCropsLoading(true);
        ws.send(JSON.stringify({
          command: 'getOtherEnableMultiDocsSplit',
          caseid: caseid
        }));
        ws.send(JSON.stringify({
          command: 'getOtherEnableProcessOnFileChange',
          caseid: caseid
        }));
        ws.send(JSON.stringify({
          command: 'getOtherPreferImageCreationTime',
          caseid: caseid
        }));
        ws.send(JSON.stringify({
          command: 'getOtherShowCalendarWidget',
          caseid: caseid
        }));
        ws.send(JSON.stringify({
          command: 'getOtherShowTrendWidget',
          caseid: caseid
        }));
        ws.send(JSON.stringify({
          command: 'getOtherFileIngestionFilter',
          caseid: caseid
        }));
      } catch (error) {
        console.error('Error sending loadOtherSettings commands:', error);
      }
    }
  }, [ws, caseid]);

  useEffect(() => {
    if (!caseid) {
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      fetchProject(caseid),
      fetchProjectPdfVisionPageCrops(caseid),
    ])
      .then(([payload, pdfVisionPageCropsPayload]) => {
        if (cancelled) {
          return;
        }
        const projectCase = payload?.case;
        if (!projectCase) {
          return;
        }
        setIsPublic(projectCase.isPublic || false);
        const nextPublicAccessLevel = normalizePublicAccessLevel(projectCase.publicAccessLevel);
        setPublicAccessLevel(nextPublicAccessLevel);
        setPublicAccessFlags(publicAccessFlagsFromLevel(nextPublicAccessLevel));
        setIsPublicLoading(false);
        const derivedPreset = projectCase.project_pdf_auto_rotate_preset
          ? normalizePdfAutoRotatePreset(projectCase.project_pdf_auto_rotate_preset)
          : (projectCase.project_pdf_auto_rotate_use_easyocr ? 'balanced' : 'fast');
        setProjectPdfAutoRotatePreset(derivedPreset);
        setProjectPdfAutoRotateOcrAvailable(
          projectCase.ocr_rotation_available === undefined || projectCase.ocr_rotation_available === null
            ? null
            : Boolean(projectCase.ocr_rotation_available)
        );
        setProjectPdfAutoRotatePresetLoading(false);
        setProjectPdfVisionPageCrops(!!pdfVisionPageCropsPayload?.project_pdf_vision_page_crops);
        setProjectPdfVisionPageCropsLoading(false);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error loading project data:', error);
          setIsPublicLoading(false);
          setProjectPdfAutoRotatePresetLoading(false);
          setProjectPdfAutoRotateOcrAvailable(null);
          setProjectPdfVisionPageCropsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [caseid]);

  const loadProjectCollections = useCallback(() => {
    if (!caseid) {
      setCollectionsLoading(false);
      return;
    }
    setCollectionsLoading(true);
    fetchProjectCollections(caseid)
      .then((payload) => {
        setProjectCollections(Array.isArray(payload.collections) ? payload.collections : []);
      })
      .catch((error) => {
        console.error('Error loading project collections:', error);
        setProjectCollections([]);
        setSnackbar({
          open: true,
          message: t('collection.load_error', 'Failed to load project collections'),
          severity: 'error'
        });
      })
      .finally(() => {
        setCollectionsLoading(false);
      });
  }, [caseid, t]);

  const loadAllCollections = useCallback(() => {
    setCollectionsLoading(true);
    fetchCollections()
      .then((payload) => {
        setAllCollections(Array.isArray(payload.collections) ? payload.collections : []);
      })
      .catch((error) => {
        console.error('Error loading all collections:', error);
        setAllCollections([]);
      })
      .finally(() => {
        setCollectionsLoading(false);
      });
  }, []);

  const withFilterIds = (list) => (list || []).map((entry) => {
    if (entry && typeof entry === 'object' && entry.id !== undefined && entry.id !== null) {
      return entry;
    }
    const id = fileFilterNextId.current;
    fileFilterNextId.current += 1;
    return {
      id,
      mode: entry?.mode || '',
      pattern: entry?.pattern || ''
    };
  });
  const normalizeFilters = (list) => (list || []).reduce((acc, entry) => {
    if (!entry || typeof entry !== 'object') {
      return acc;
    }
    const mode = String(entry.mode || '').trim().toLowerCase();
    const pattern = String(entry.pattern || '').trim();
    if (!mode || !pattern) {
      return acc;
    }
    if (!['contains', 'ignore', 'regex'].includes(mode)) {
      return acc;
    }
    acc.push({ mode, pattern });
    return acc;
  }, []);
  const normalizedFileFilters = useMemo(
    () => normalizeFilters(fileFilters),
    [fileFilters]
  );
  const fileFilterHasChanges = JSON.stringify(normalizedFileFilters) !== JSON.stringify(savedFileFilters);
  const fileFilterErrors = useMemo(() => {
    const nextErrors = {};
    fileFilters.forEach((filter) => {
      const mode = String(filter?.mode || '').trim();
      const pattern = String(filter?.pattern || '').trim();

      // Skip completely empty rows
      if (!mode && !pattern) {
        return;
      }

      // Mode validation
      if (!mode) {
        nextErrors[`${filter.id}-mode`] = t('settings.others.file_filter_mode_required', 'Select a match mode');
      }

      // Pattern validation
      if (!pattern) {
        nextErrors[`${filter.id}-pattern`] = t('settings.others.file_filter_pattern_required', 'Pattern is required for this mode');
      } else if (mode === 'regex') {
        try {
          new RegExp(pattern);
        } catch (error) {
          nextErrors[`${filter.id}-pattern`] = t('settings.others.file_filter_regex_invalid', 'Invalid regex pattern');
        }
      }
    });
    return nextErrors;
  }, [fileFilters, t]);
  const fileFilterHasErrors = useMemo(
    () => Object.values(fileFilterErrors).some(Boolean),
    [fileFilterErrors]
  );
  const publicUrl = useMemo(() => {
    if (typeof window === 'undefined' || !caseid) {
      return '';
    }
    return `${window.location.origin}/public/case/${caseid}`;
  }, [caseid]);

  const compactCardSx = {
    width: '100%',
    borderRadius: 2,
    border: '1px solid rgba(15, 23, 42, 0.08)',
    backgroundColor: '#fff',
    boxShadow: '0 1px 6px rgba(15, 23, 42, 0.07)',
    p: { xs: 1.5, sm: 2 },
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  };

  // Load feature settings when component mounts
  useEffect(() => {
    if (ws && caseid) {
      loadOtherSettings();
      loadProjectCollections();
      loadAllCollections();
    } else if (!ws) {
      setCollectionsLoading(false);
      setIsLoading(false);
      setProcessOnFileChangeLoading(false);
      setPreferImageCreationTimeLoading(false);
      setProjectPdfVisionPageCropsLoading(false);
      setShowCalendarWidgetLoading(false);
      setShowTrendWidgetLoading(false);
      setFileFilterLoading(false);
    }
  }, [ws, caseid, loadOtherSettings, loadProjectCollections, loadAllCollections]);

  // Timeout mechanism to prevent stuck loading states
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (collectionsLoading) {
        setCollectionsLoading(false);
        console.warn('Collection loading timed out - WebSocket may not be responding');
      }
      if (isLoading) {
        setIsLoading(false);
        console.warn('Settings loading timed out - WebSocket may not be responding');
      }
      if (processOnFileChangeLoading) {
        setProcessOnFileChangeLoading(false);
        console.warn('Process-on-file-change loading timed out - WebSocket may not be responding');
      }
      if (preferImageCreationTimeLoading) {
        setPreferImageCreationTimeLoading(false);
        console.warn('Image time preference loading timed out - WebSocket may not be responding');
      }
      if (projectPdfAutoRotatePresetLoading) {
        setProjectPdfAutoRotatePresetLoading(false);
        console.warn('PDF auto-rotate loading timed out - WebSocket may not be responding');
      }
      if (projectPdfVisionPageCropsLoading) {
        setProjectPdfVisionPageCropsLoading(false);
        console.warn('PDF vision crops loading timed out - WebSocket may not be responding');
      }
      if (showCalendarWidgetLoading) {
        setShowCalendarWidgetLoading(false);
        console.warn('Calendar widget visibility loading timed out - WebSocket may not be responding');
      }
      if (showTrendWidgetLoading) {
        setShowTrendWidgetLoading(false);
        console.warn('Trend widget visibility loading timed out - WebSocket may not be responding');
      }
      if (fileFilterLoading) {
        setFileFilterLoading(false);
        console.warn('File filter loading timed out - WebSocket may not be responding');
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [collectionsLoading, isLoading, processOnFileChangeLoading, preferImageCreationTimeLoading, projectPdfAutoRotatePresetLoading, projectPdfVisionPageCropsLoading, showCalendarWidgetLoading, showTrendWidgetLoading, fileFilterLoading]);

  // Handle WebSocket messages
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.command === 'getOtherEnableMultiDocsSplit') {
          let payload = data.data;
          if (payload.status === 'success') {
            setMultiDocsSplitEnabled(payload.enable_multi_docs_split);
            setIsLoading(false);
          } else {
            console.error('Error loading other settings:', data.message);
            setSnackbar({
              open: true,
              message: t('settings.others.load_error'),
              severity: 'error'
            });
            setIsLoading(false);
          }
        } else if (data.command === 'updateOtherEnableMultiDocsSplit') {
          let payload = data.data;
          if (payload.status === 'success') {
            setMultiDocsSplitEnabled(payload.enable_multi_docs_split);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
        } else if (data.command === 'getOtherEnableProcessOnFileChange') {
          let payload = data.data;
          if (payload.status === 'success') {
            setProcessOnFileChangeEnabled(payload.enable_process_on_file_change);
            setProcessOnFileChangeLoading(false);
          } else {
            console.error('Error loading process-on-file-change setting:', data.message || payload?.message);
            setSnackbar({
              open: true,
              message: t('settings.others.load_error'),
              severity: 'error'
            });
            setProcessOnFileChangeLoading(false);
          }
        } else if (data.command === 'updateOtherEnableProcessOnFileChange') {
          let payload = data.data;
          if (payload.status === 'success') {
            setProcessOnFileChangeEnabled(payload.enable_process_on_file_change);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setProcessOnFileChangeLoading(false);
        } else if (data.command === 'getOtherPreferImageCreationTime') {
          let payload = data.data;
          if (payload.status === 'success') {
            setPreferImageCreationTime(!!payload.prefer_image_creation_time);
            setPreferImageCreationTimeLoading(false);
          } else {
            console.error('Error loading preferImageCreationTime setting:', data.message || payload?.message);
            setSnackbar({
              open: true,
              message: t('settings.others.load_error'),
              severity: 'error'
            });
            setPreferImageCreationTimeLoading(false);
          }
        } else if (data.command === 'updateOtherPreferImageCreationTime') {
          let payload = data.data;
          if (payload.status === 'success') {
            setPreferImageCreationTime(payload.prefer_image_creation_time);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setPreferImageCreationTimeLoading(false);
        } else if (data.command === 'updateOtherProjectPdfAutoRotatePreset') {
          let payload = data.data;
          if (payload.status === 'success') {
            const preset = normalizePdfAutoRotatePreset(
              payload.project_pdf_auto_rotate_preset,
              'fast'
            );
            setProjectPdfAutoRotatePreset(preset);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setProjectPdfAutoRotatePresetLoading(false);
        } else if (data.command === 'updateOtherProjectPdfAutoRotateUseEasyocr') {
          let payload = data.data;
          if (payload.status === 'success') {
            const preset = payload.project_pdf_auto_rotate_preset
              ? normalizePdfAutoRotatePreset(payload.project_pdf_auto_rotate_preset)
              : (payload.project_pdf_auto_rotate_use_easyocr ? 'balanced' : 'fast');
            setProjectPdfAutoRotatePreset(preset);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setProjectPdfAutoRotatePresetLoading(false);
        } else if (data.command === 'getOtherShowCalendarWidget') {
          let payload = data.data;
          if (payload.status === 'success') {
            setShowCalendarWidget(payload.show_calendar_widget);
          } else {
            console.error('Error loading calendar widget visibility:', data.message || payload?.message);
            setSnackbar({
              open: true,
              message: t('settings.others.load_error'),
              severity: 'error'
            });
          }
          setShowCalendarWidgetLoading(false);
        } else if (data.command === 'updateOtherShowCalendarWidget') {
          let payload = data.data;
          if (payload.status === 'success') {
            setShowCalendarWidget(payload.show_calendar_widget);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setShowCalendarWidgetLoading(false);
        } else if (data.command === 'getOtherShowTrendWidget') {
          let payload = data.data;
          if (payload.status === 'success') {
            setShowTrendWidget(payload.show_trend_widget);
          } else {
            console.error('Error loading trend widget visibility:', data.message || payload?.message);
            setSnackbar({
              open: true,
              message: t('settings.others.load_error'),
              severity: 'error'
            });
          }
          setShowTrendWidgetLoading(false);
        } else if (data.command === 'updateOtherShowTrendWidget') {
          let payload = data.data;
          if (payload.status === 'success') {
            setShowTrendWidget(payload.show_trend_widget);
            setSnackbar({
              open: true,
              message: t('settings.others.update_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: t('settings.others.update_error'),
              severity: 'error'
            });
          }
          setShowTrendWidgetLoading(false);
        } else if (data.command === 'getOtherFileIngestionFilter') {
          let payload = data.data;
          if (payload.status === 'success') {
            const payloadFilters = Array.isArray(payload.file_filters) ? payload.file_filters : [];
            let filters = payloadFilters;
            if (filters.length === 0) {
              const fallbackMode = payload.file_filter_mode || '';
              const fallbackPattern = (payload.file_filter_pattern || '').trim();
              if (fallbackMode || fallbackPattern) {
                filters = [{ mode: fallbackMode, pattern: fallbackPattern }];
              }
            }
            const normalized = normalizeFilters(filters);
            setFileFilters(withFilterIds(filters));
            setSavedFileFilters(normalized);
          } else {
            setSnackbar({
              open: true,
              message: payload.message || t('settings.others.file_filter_load_error', 'Failed to load file filter'),
              severity: 'error'
            });
          }
          setFileFilterLoading(false);
        } else if (data.command === 'updateOtherFileIngestionFilter') {
          let payload = data.data;
          setFileFilterSaving(false);
          if (payload.status === 'success') {
            const payloadFilters = Array.isArray(payload.file_filters) ? payload.file_filters : [];
            let filters = payloadFilters;
            if (filters.length === 0) {
              const fallbackMode = payload.file_filter_mode || '';
              const fallbackPattern = (payload.file_filter_pattern || '').trim();
              if (fallbackMode || fallbackPattern) {
                filters = [{ mode: fallbackMode, pattern: fallbackPattern }];
              }
            }
            const normalized = normalizeFilters(filters);
            setFileFilters(withFilterIds(filters));
            setSavedFileFilters(normalized);
            setSnackbar({
              open: true,
              message: t('settings.others.file_filter_update_success', 'File filter updated'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: payload.message || t('settings.others.file_filter_update_error', 'Failed to update file filter'),
              severity: 'error'
            });
          }
        } else if (data.command === 'update_case_public_response') {
          let payload = data.data;
          if (payload && payload.success) {
            setIsPublic(payload.isPublic);
            const nextPublicAccessLevel = normalizePublicAccessLevel(payload.publicAccessLevel);
            setPublicAccessLevel(nextPublicAccessLevel);
            setPublicAccessFlags(publicAccessFlagsFromLevel(nextPublicAccessLevel));
            setSnackbar({
              open: true,
              message: t('settings.others.public_status_updated', 'Public status updated successfully'),
              severity: 'success'
            });
          } else if (payload && payload.error) {
            setSnackbar({
              open: true,
              message: payload.error,
              severity: 'error'
            });
          }
        } else if (data.command === 'addProjectToCollection') {
          let payload = data.data || data; // Handle both response formats
          if (payload && payload.status === 'success') {
            setSnackbar({
              open: true,
              message: t('collection.case_added_success', 'Project added to collection successfully'),
              severity: 'success'
            });
            loadProjectCollections(); // Refresh the project collections
          } else {
            const errorMessage = payload?.message || 'Unknown error adding project to collection';
            console.error('Error adding project to collection:', errorMessage);
            setSnackbar({
              open: true,
              message: t('collection.add_error', 'Failed to add project to collection') + ': ' + errorMessage,
              severity: 'error'
            });
          }
        } else if (data.command === 'removeProjectFromCollection') {
          let payload = data.data || data; // Handle both response formats
          if (payload && payload.status === 'success') {
            setSnackbar({
              open: true,
              message: t('collection.remove_success', 'Project removed from collection successfully'),
              severity: 'success'
            });
            loadProjectCollections(); // Refresh the project collections
          } else {
            const errorMessage = payload?.message || 'Unknown error removing project from collection';
            console.error('Error removing project from collection:', errorMessage);
            setSnackbar({
              open: true,
              message: t('collection.remove_error', 'Failed to remove project from collection') + ': ' + errorMessage,
              severity: 'error'
            });
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws, t, loadProjectCollections]);

  const handleMultiDocsSplitToggle = (event) => {
    const newValue = event.target.checked;
    setMultiDocsSplitEnabled(newValue);

    if (ws) {
      ws.send(JSON.stringify({
        command: 'updateOtherEnableMultiDocsSplit',
        caseid: caseid,
        enable_multi_docs_split: newValue
      }));
    }
  };

  const handleProcessOnFileChangeToggle = (event) => {
    const newValue = event.target.checked;
    setProcessOnFileChangeEnabled(newValue);
    setProcessOnFileChangeLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'updateOtherEnableProcessOnFileChange',
          caseid: caseid,
          enable_process_on_file_change: newValue
        }));
      } catch (error) {
        console.error('Error sending updateOtherEnableProcessOnFileChange:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setProcessOnFileChangeLoading(false);
      }
    } else {
      setProcessOnFileChangeLoading(false);
    }
  };

  const handlePreferImageCreationTimeToggle = (event) => {
    const newValue = event.target.checked;
    setPreferImageCreationTime(newValue);
    setPreferImageCreationTimeLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'updateOtherPreferImageCreationTime',
          caseid: caseid,
          prefer_image_creation_time: newValue
        }));
      } catch (error) {
        console.error('Error sending updateOtherPreferImageCreationTime:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setPreferImageCreationTimeLoading(false);
      }
    } else {
      setPreferImageCreationTimeLoading(false);
    }
  };

  const handleProjectPdfAutoRotatePresetChange = (event) => {
    const newValue = normalizePdfAutoRotatePreset(event.target.value);
    setProjectPdfAutoRotatePreset(newValue);
    setProjectPdfAutoRotatePresetLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'updateOtherProjectPdfAutoRotatePreset',
          caseid: caseid,
          project_pdf_auto_rotate_preset: newValue
        }));
      } catch (error) {
        console.error('Error sending updateOtherProjectPdfAutoRotatePreset:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setProjectPdfAutoRotatePresetLoading(false);
      }
    } else {
      setProjectPdfAutoRotatePresetLoading(false);
    }
  };

  const handleProjectPdfVisionPageCropsToggle = async (event) => {
    const newValue = event.target.checked;
    setProjectPdfVisionPageCrops(newValue);
    setProjectPdfVisionPageCropsLoading(true);

    try {
      await updateProjectPdfVisionPageCrops(caseid, newValue);
      setSnackbar({
        open: true,
        message: t('settings.others.update_success'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating projectPdfVisionPageCrops via HTTP:', error);
      setProjectPdfVisionPageCrops(!newValue);
      setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
    } finally {
      setProjectPdfVisionPageCropsLoading(false);
    }
  };

  const handleShowCalendarWidgetToggle = (event) => {
    const newValue = event.target.checked;
    setShowCalendarWidget(newValue);
    setShowCalendarWidgetLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'updateOtherShowCalendarWidget',
          caseid: caseid,
          show_calendar_widget: newValue
        }));
      } catch (error) {
        console.error('Error sending updateOtherShowCalendarWidget:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setShowCalendarWidgetLoading(false);
      }
    } else {
      setShowCalendarWidgetLoading(false);
    }
  };

  const handleShowTrendWidgetToggle = (event) => {
    const newValue = event.target.checked;
    setShowTrendWidget(newValue);
    setShowTrendWidgetLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'updateOtherShowTrendWidget',
          caseid: caseid,
          show_trend_widget: newValue
        }));
      } catch (error) {
        console.error('Error sending updateOtherShowTrendWidget:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setShowTrendWidgetLoading(false);
      }
    } else {
      setShowTrendWidgetLoading(false);
    }
  };

  const handleFileFilterChange = (id, field, value) => {
    setFileFilters((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    );
  };

  const handleAddFileFilter = () => {
    const id = fileFilterNextId.current;
    fileFilterNextId.current += 1;
    setFileFilters((prev) => [...prev, { id, mode: '', pattern: '' }]);
  };

  const handleRemoveFileFilter = (id) => {
    setFileFilters((prev) => prev.filter((entry) => entry.id !== id));
  };

  const moveFileFilter = (index, direction) => {
    setFileFilters((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) {
        return prev;
      }
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleFileFilterSave = () => {
    if (!ws || !caseid) {
      setSnackbar({
        open: true,
        message: t('settings.others.websocket_missing', 'WebSocket connection unavailable'),
        severity: 'error'
      });
      return;
    }

    setFileFilterSaving(true);
    ws.send(JSON.stringify({
      command: 'updateOtherFileIngestionFilter',
      caseid: caseid,
      file_filters: normalizedFileFilters
    }));
  };

  const handleIsPublicToggle = (event) => {
    const newValue = event.target.checked;

    // If turning ON public access, show warning dialog
    if (newValue) {
      setPublicWarningDialogOpen(true);
      return;
    }

    // If turning OFF, proceed without warning
    updatePublicStatus(false);
  };

  const updatePublicStatus = (newValue, accessLevel = publicAccessLevel) => {
    setIsPublic(newValue);
    setIsPublicLoading(true);

    if (ws) {
      try {
        ws.send(JSON.stringify({
          command: 'update_case_public',
          caseid: caseid,
          isPublic: newValue,
          publicAccessLevel: normalizePublicAccessLevel(accessLevel)
        }));
        setIsPublicLoading(false);
      } catch (error) {
        console.error('Error sending update_case_public:', error);
        setSnackbar({ open: true, message: t('settings.others.update_error'), severity: 'error' });
        setIsPublicLoading(false);
      }
    } else {
      setIsPublicLoading(false);
    }
  };

  const handlePublicWarningConfirm = () => {
    setPublicWarningDialogOpen(false);
    updatePublicStatus(true);
  };

  const handlePublicAccessToggle = (mode) => () => {
    setPublicAccessFlags((prev) => {
      const next = {
        ...prev,
        [mode]: !prev[mode]
      };
      if (!next.canView && !next.canUpload) {
        return prev;
      }
      const nextLevel = publicAccessLevelFromFlags(next);
      setPublicAccessLevel(nextLevel);
      if (isPublic) {
        updatePublicStatus(true, nextLevel);
      }
      return next;
    });
  };

  const handlePublicWarningCancel = () => {
    setPublicWarningDialogOpen(false);
    // Keep toggle in OFF position
  };

  const handleCopyPublicUrl = useCallback(async () => {
    if (!publicUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      setSnackbar({
        open: true,
        message: t('clipboard_copied', 'Copied to clipboard'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy public link:', error);
      setSnackbar({
        open: true,
        message: t('settings.others.update_error', 'Failed to copy public link'),
        severity: 'error'
      });
    }
  }, [publicUrl, t]);

  const projectPdfAutoRotateSupportsOcr = projectPdfAutoRotateOcrAvailable !== false;
  const projectPdfAutoRotateAvailabilityMessage = !projectPdfAutoRotateSupportsOcr
    ? t(
        'settings.others.pdf_auto_rotate_ocr_unavailable',
        'OCR-backed rotation is unavailable in this runtime, so Balanced and Quality are disabled.'
      )
    : '';

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddToCollection = () => {
    setAddToCollectionDialogOpen(true);
  };

  const handleAddToCollectionConfirm = (collectionId) => {
    if (ws && caseid && collectionId) {
      ws.send(JSON.stringify({
        command: 'addProjectToCollection',
        project_id: caseid,
        collection_id: collectionId
      }));
    }
    setAddToCollectionDialogOpen(false);
  };

  const handleAddToCollectionCancel = () => {
    setAddToCollectionDialogOpen(false);
  };

  const handleRemoveFromCollection = (collectionId) => {
    if (ws && caseid && collectionId) {
      ws.send(JSON.stringify({
        command: 'removeProjectFromCollection',
        project_id: caseid,
        collection_id: collectionId
      }));
    }
  };

  // Filter available collections (exclude ones the project is already in)
  const availableCollections = allCollections.filter(collection =>
    !projectCollections.some(projCol => projCol.id === collection.id)
  );

  return (
    <>
      <Fade in={true} timeout={600}>
        <Box sx={{ mb: { xs: 1, sm: 2 } }}>
          <Typography variant={isMobile ? "h6" : "h5"} gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {t("settings.others.title")}
            <Tooltip title={t("settings.others.description")}>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>
      </Fade>

      <Fade in={isVisible} timeout={800}>
        <Box
          sx={{
            width: '100%',
            mx: 'auto',
            px: { xs: 0, sm: 1 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            maxWidth: '1100px',
          }}
        >
          {/* Multi-Document Split Section */}
          <Box sx={{ ...compactCardSx }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <SplitscreenIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.multi_docs_split', 'Multi-Document Split')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.multi_docs_split_tooltip', 'When enabled, allows processing multiple documents in a single upload with automatic splitting and organization')}
                </Typography>
              </Box>
              <Tooltip title="We will force to find multiple documents in each uploaded file.">
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('settings.others.enable_feature', 'Enable Feature')}
              </Typography>
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: '#5757b0' }} />
              ) : (
                <Switch
                  checked={multiDocsSplitEnabled}
                  onChange={handleMultiDocsSplitToggle}
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5757b0',
                      '&:hover': {
                        backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#cbd5e0',
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Process on File Change Section */}
          <Box sx={{ ...compactCardSx }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <FileUploadIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.process_on_file_change', 'Ignore modified files')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.process_on_file_change_tooltip', 'When enabled, changes to an existing file do not trigger reprocessing')}
                </Typography>
              </Box>
              <Tooltip title="When enabled, any new file detected in the folder will be automatically processed.">
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('settings.others.enable_feature', 'Enable Feature')}
              </Typography>
              {processOnFileChangeLoading ? (
                <CircularProgress size={24} sx={{ color: '#5757b0' }} />
              ) : (
                <Switch
                  checked={processOnFileChangeEnabled}
                  onChange={handleProcessOnFileChangeToggle}
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5757b0',
                      '&:hover': {
                        backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#cbd5e0',
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* File Ingestion Filter Section */}
          <Box sx={{ ...compactCardSx, gridColumn: '1/-1' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <FilterAltIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.file_filter_title', 'File Ingestion Filter')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.file_filter_tooltip', 'Filter which files are ingested by name or extension')}
                </Typography>
              </Box>
              <Tooltip title={t('settings.others.file_filter_info', 'Applies to file name and extension only.')}>
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t(
                  'settings.others.file_filter_rules_hint',
                  'Rules are checked top-to-bottom. First match wins. If you add any include rules (Contains/Regex), only matching files are ingested unless an earlier Ignore rule matches.'
                )}
              </Alert>
              {fileFilterLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#5757b0' }} />
                </Box>
              ) : fileFilters.length === 0 ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px dashed rgba(87, 87, 176, 0.3)',
                    p: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('settings.others.file_filter_empty', 'No filters yet. Add a rule to start filtering.')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddFileFilter}
                    sx={{ borderColor: '#5757b0', color: '#5757b0' }}
                  >
                    {t('settings.others.file_filter_add_rule', 'Add rule')}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {fileFilters.map((filter, index) => (
                    <Box
                      key={filter.id ?? index}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        alignItems: 'center',
                        p: 0.5,
                        borderRadius: 2,
                        border: '1px solid rgba(87, 87, 176, 0.15)',
                        backgroundColor: 'rgba(87, 87, 176, 0.04)'
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#5757b0', fontWeight: 600 }}>
                        {t('settings.others.file_filter_rule_label', 'Rule')} {index + 1}
                      </Typography>
                      <TextField
                        select
                        margin="dense"
                        label={t('settings.others.file_filter_mode_label', 'Match mode')}
                        value={filter.mode || ''}
                        onChange={(event) => handleFileFilterChange(filter.id, 'mode', event.target.value)}
                        size="small"
                        disabled={fileFilterSaving}
                        error={!!fileFilterErrors[`${filter.id}-mode`]}
                        helperText={fileFilterErrors[`${filter.id}-mode`] || ''}
                        FormHelperTextProps={{ sx: { m: 0, mt: '2px', lineHeight: 1, fontSize: '0.72rem', minHeight: '18px', visibility: fileFilterErrors[`${filter.id}-mode`] ? 'visible' : 'hidden' } }}
                        sx={{ flex: '1 1 120px', minWidth: 120 }}
                      >
                        <MenuItem value="contains">{t('settings.others.file_filter_mode_contains', 'Contains')}</MenuItem>
                        <MenuItem value="ignore">{t('settings.others.file_filter_mode_ignore', 'Ignore')}</MenuItem>
                        <MenuItem value="regex">{t('settings.others.file_filter_mode_regex', 'Regex')}</MenuItem>
                      </TextField>
                      <TextField
                        margin="dense"
                        label={t('settings.others.file_filter_pattern_label', 'Pattern')}
                        value={filter.pattern || ''}
                        onChange={(event) => handleFileFilterChange(filter.id, 'pattern', event.target.value)}
                        size="small"
                        disabled={fileFilterSaving}
                        error={!!fileFilterErrors[`${filter.id}-pattern`]}
                        helperText={fileFilterErrors[`${filter.id}-pattern`] || ''}
                        FormHelperTextProps={{ sx: { m: 0, mt: '2px', lineHeight: 1, fontSize: '0.72rem', minHeight: '18px', visibility: fileFilterErrors[`${filter.id}-pattern`] ? 'visible' : 'hidden' } }}
                        placeholder={t('settings.others.file_filter_pattern_placeholder', 'e.g. .pdf, invoice')}
                        sx={{ flex: '1.2 1 160px', minWidth: 160 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <Tooltip title={t('settings.others.file_filter_move_up', 'Move up')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => moveFileFilter(index, -1)}
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('settings.others.file_filter_move_down', 'Move down')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => moveFileFilter(index, 1)}
                              disabled={index === fileFilters.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('settings.others.file_filter_remove', 'Remove rule')}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFileFilter(filter.id)}
                            sx={{ color: '#ff6666' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddFileFilter}
                  disabled={fileFilterLoading || fileFilterSaving}
                  sx={{ borderColor: '#5757b0', color: '#5757b0' }}
                >
                  {t('settings.others.file_filter_add_rule', 'Add rule')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleFileFilterSave}
                  disabled={fileFilterLoading || fileFilterSaving || !fileFilterHasChanges || fileFilterHasErrors}
                  startIcon={fileFilterSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{
                    backgroundColor: '#5757b0',
                    '&:hover': { backgroundColor: '#7777d0' },
                  }}
                >
                  {t('settings.others.file_filter_save', 'Save filters')}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Image Time Preference Section */}
          <Box sx={{ ...compactCardSx }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <PhotoCameraIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.image_time_preference', 'Image Date Fallback')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.image_time_preference_tooltip', 'Use file modified time when an image has no date metadata (falls back to creation time)')}
                </Typography>
              </Box>
              <Tooltip title="If the image has no date metadata, use the file modification time (or creation time if missing).">
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('settings.others.enable_feature', 'Enable Feature')}
              </Typography>
              {preferImageCreationTimeLoading ? (
                <CircularProgress size={24} sx={{ color: '#5757b0' }} />
              ) : (
                <Switch
                  checked={preferImageCreationTime}
                  onChange={handlePreferImageCreationTimeToggle}
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5757b0',
                      '&:hover': {
                        backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#cbd5e0',
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* PDF/Image Rotation Section */}
          <Box sx={{ ...compactCardSx }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <RefreshIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.pdf_auto_rotate_title', 'OCR-assisted document rotation')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.pdf_auto_rotate_tooltip', 'Uses OCR as an extra signal to choose the best orientation for PDF pages and supported images in this project.')}
                </Typography>
              </Box>
              <Tooltip title={t('settings.others.pdf_auto_rotate_info', 'Applies during first-pass ingestion and related vision processing.')}>
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                size={isMobile ? "small" : "medium"}
                label={t('settings.others.pdf_auto_rotate_level', 'Rotation level')}
                value={projectPdfAutoRotatePreset}
                onChange={handleProjectPdfAutoRotatePresetChange}
                disabled={projectPdfAutoRotatePresetLoading}
                helperText={t('settings.others.pdf_auto_rotate_level_helper', 'Controls how aggressively OCR is used to normalize page and image orientation during ingestion.')}
                SelectProps={{
                  renderValue: (val) => {
                    const preset = normalizePdfAutoRotatePreset(val);
                    return t(`settings.others.pdf_auto_rotate_level_${preset}`, preset);
                  }
                }}
              >
                <MenuItem value="off">{t('settings.others.pdf_auto_rotate_level_off', 'Off')}</MenuItem>
                <MenuItem value="fast">{t('settings.others.pdf_auto_rotate_level_fast', 'Fast')}</MenuItem>
                <MenuItem value="balanced" disabled={!projectPdfAutoRotateSupportsOcr}>
                  {t('settings.others.pdf_auto_rotate_level_balanced', 'Balanced')}
                </MenuItem>
                <MenuItem value="quality" disabled={!projectPdfAutoRotateSupportsOcr}>
                  {t('settings.others.pdf_auto_rotate_level_quality', 'Quality')}
                </MenuItem>
              </TextField>
              {!projectPdfAutoRotateSupportsOcr && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {projectPdfAutoRotateAvailabilityMessage}
                </Alert>
              )}
            </Box>
          </Box>

          <Box sx={{ ...compactCardSx }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1f7a8c 0%, #4fb3c8 100%)',
                  boxShadow: '0 4px 15px rgba(31, 122, 140, 0.28)',
                  flexShrink: 0
                }}
              >
                <PhotoCameraIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.pdf_vision_page_crops_title', 'Improve Vision Recognition (Forms)')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.pdf_vision_page_crops_tooltip', 'Improve the vision model so it can read dense or low-resolution areas more reliably. (Cost more tokens)')}
                </Typography>
              </Box>
              <Tooltip title={t('settings.others.pdf_vision_page_crops_info', 'Uses extra crops as supplemental visual references when processing PDF pages.')}>
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(31, 122, 140, 0.6)',
                    '&:hover': {
                      color: '#1f7a8c',
                      backgroundColor: 'rgba(31, 122, 140, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('settings.others.pdf_vision_page_crops_enable', 'Enable PDF page crops')}
              </Typography>
              {projectPdfVisionPageCropsLoading ? (
                <CircularProgress size={24} sx={{ color: '#1f7a8c' }} />
              ) : (
                <Switch
                  checked={projectPdfVisionPageCrops}
                  onChange={handleProjectPdfVisionPageCropsToggle}
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#1f7a8c',
                      '&:hover': {
                        backgroundColor: 'rgba(31, 122, 140, 0.08)',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#cbd5e0',
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Widget Dashboard Sections */}
          <Box sx={{ ...compactCardSx }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <CalendarMonthIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.dashboard_widgets_title', 'Widget Dashboard Sections')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.dashboard_widgets_tooltip', 'Show or hide calendar and trend sections in the widget dashboard')}
                </Typography>
              </Box>
              <Tooltip title={t('settings.others.dashboard_widgets_info', 'These controls affect the widget dashboard view only.')}>
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonthIcon sx={{ color: '#5757b0', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {t('settings.others.dashboard_show_calendar', 'Show calendar widget')}
                  </Typography>
                </Box>
                {showCalendarWidgetLoading ? (
                  <CircularProgress size={24} sx={{ color: '#5757b0' }} />
                ) : (
                  <Switch
                    checked={showCalendarWidget}
                    onChange={handleShowCalendarWidgetToggle}
                    size={isMobile ? "small" : "medium"}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5757b0',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                        },
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#cbd5e0',
                      },
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: '#5757b0', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {t('settings.others.dashboard_show_trend', 'Show event trends widget')}
                  </Typography>
                </Box>
                {showTrendWidgetLoading ? (
                  <CircularProgress size={24} sx={{ color: '#5757b0' }} />
                ) : (
                  <Switch
                    checked={showTrendWidget}
                    onChange={handleShowTrendWidgetToggle}
                    size={isMobile ? "small" : "medium"}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5757b0',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                        },
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#cbd5e0',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Public Access Section */}
          <Box sx={{ ...compactCardSx }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <PublicIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.public_access', 'Public Access')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.public_access_tooltip', 'Make this project publicly accessible via a shared link')}
                </Typography>
              </Box>
              <Tooltip title="Allow anyone with the link to view this project.">
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {t('settings.others.enable_public_access', 'Enable Public Access')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PublicAccessPermissionIcons
                  accessFlags={publicAccessFlags}
                  disabled={isPublicLoading}
                  onToggleView={handlePublicAccessToggle('canView')}
                  onToggleUpload={handlePublicAccessToggle('canUpload')}
                />
                {isPublicLoading ? (
                  <CircularProgress size={24} sx={{ color: '#5757b0' }} />
                ) : (
                  <Switch
                    checked={isPublic}
                    onChange={handleIsPublicToggle}
                    size={isMobile ? "small" : "medium"}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5757b0',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                        },
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#cbd5e0',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {isPublic && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('settings.others.public_link_info', 'This project is currently public.')}
                </Alert>
                {publicUrl && (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={publicUrl}
                      InputProps={{ readOnly: true }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCopyPublicUrl}
                      sx={{
                        whiteSpace: 'nowrap',
                        color: '#5757b0',
                        borderColor: '#5757b0',
                        '&:hover': {
                          borderColor: '#4338ca',
                          backgroundColor: 'rgba(87, 87, 176, 0.04)',
                        }
                      }}
                    >
                      {t('project_share.copy_public_link', 'Copy Link')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Collections Section */}
          <Box sx={{ ...compactCardSx, gridColumn: '1/-1' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                  boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                  flexShrink: 0
                }}
              >
                <FolderSpecialIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                  {t('settings.others.collections', 'Collections')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                  {t('settings.others.collections_tooltip', 'Manage which collections this project belongs to')}
                </Typography>
              </Box>
              <Tooltip title="Add this project to collections for better organization.">
                <IconButton
                  size="small"
                  sx={{
                    color: 'rgba(87, 87, 176, 0.6)',
                    '&:hover': {
                      color: '#5757b0',
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Content */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {collectionsLoading ? (
                  <CircularProgress size={24} sx={{ color: '#5757b0' }} />
                ) : projectCollections.length > 0 ? (
                  projectCollections.map((collection) => (
                    <Chip
                      key={collection.id}
                      label={collection.title}
                      onDelete={() => handleRemoveFromCollection(collection.id)}
                      deleteIcon={<RemoveIcon />}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        backgroundColor: '#e0e7ff',
                        color: '#4338ca',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: '#6366f1',
                          '&:hover': {
                            color: '#4338ca',
                          },
                        },
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {t('settings.others.no_collections', 'Not in any collections')}
                  </Typography>
                )}
              </Box>

              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={<AddIcon />}
                onClick={handleAddToCollection}
                sx={{
                  color: '#5757b0',
                  borderColor: '#5757b0',
                  '&:hover': {
                    borderColor: '#4338ca',
                    backgroundColor: 'rgba(87, 87, 176, 0.04)',
                  },
                }}
              >
                {t('settings.others.add_to_collection', 'Add to Collection')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Add to Collection Dialog */}
      <AddToCollectionDialog
        open={addToCollectionDialogOpen}
        onClose={handleAddToCollectionCancel}
        onConfirm={handleAddToCollectionConfirm}
        collections={availableCollections}
      />

      {/* Public Warning Dialog */}
      <Dialog
        open={publicWarningDialogOpen}
        onClose={handlePublicWarningCancel}
        aria-labelledby="public-warning-dialog-title"
        aria-describedby="public-warning-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 10,
          }
        }}
      >
        <DialogTitle id="public-warning-dialog-title" sx={{ bgcolor: '#ff9800', color: 'white', display: 'flex', alignItems: 'center' }}>
          <PublicIcon sx={{ mr: 1 }} /> {t('settings.others.public_warning_title', 'Make Project Public?')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              {t('settings.others.public_warning_text', 'Making this project public will allow anyone with the link to view it. Are you sure you want to proceed?')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handlePublicWarningCancel} variant="outlined" color="inherit">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handlePublicWarningConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#ff9800',
              color: 'white',
              '&:hover': { backgroundColor: '#f57c00' },
            }}
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
      />
    </>
  );
};

export default SettingOthers;
